<template>
  <div>
    <!-- Filter bar -->
    <div class="d-flex align-items-center mb-2 flex-wrap" style="gap:8px">
      <div class="input-group input-group-sm" style="width:auto">
        <div class="input-group-prepend"><span class="input-group-text">Token</span></div>
        <select class="form-control form-control-sm" v-model="filterToken">
          <option value="">All</option>
          <option v-for="t in tokenOptions" :key="t" :value="t">{{ t }}</option>
        </select>
      </div>
      <div class="input-group input-group-sm" style="width:auto">
        <div class="input-group-prepend"><span class="input-group-text">Direction</span></div>
        <select class="form-control form-control-sm" v-model="filterDir">
          <option value="">All</option>
          <option value="IN">IN</option>
          <option value="OUT">OUT</option>
          <option value="CONTRACT">Contract</option>
          <option value="FAILED">Failed</option>
        </select>
      </div>
      <span class="ml-auto text-muted small">{{ filteredTxs.length }} transactions</span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status" :style="`color:${accentColor}`"></div>
      <p class="mt-2 text-muted small">Fetching transactions…</p>
    </div>

    <!-- Table -->
    <div v-else>
      <table class="table table-striped table-sm table-hover">
        <thead>
          <tr>
            <th>Date / Time</th>
            <th>Type</th>
            <th>Token</th>
            <th>Amount</th>
            <th>From / To</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="pagedTxs.length === 0 && !loading">
            <td colspan="7" class="text-center text-muted py-4">
              {{ txs.length === 0 ? 'No transactions found.' : 'No transactions match the filter.' }}
            </td>
          </tr>
          <tr v-for="tx in pagedTxs" :key="tx.hash">
            <td class="small text-nowrap">{{ formatDate(tx.timestamp) }}</td>
            <td>
              <span class="badge" :class="typeBadge(tx.type)">{{ tx.type }}</span>
            </td>
            <td><strong>{{ tx.token }}</strong></td>
            <td class="text-right">{{ tx.amount > 0 ? tx.amount.toFixed(6) : '—' }}</td>
            <td class="small" style="max-width:160px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap">
              <span :title="tx.counterparty">{{ shorten(tx.counterparty) }}</span>
            </td>
            <td>
              <span class="small" :class="tx.status === 'confirmed' ? 'text-success' : tx.status === 'failed' ? 'text-danger' : 'text-warning'">
                {{ tx.status }}
              </span>
            </td>
            <td>
              <a :href="tx.explorerUrl" target="_blank" class="btn btn-xs btn-outline-secondary" style="font-size:0.7rem; padding:1px 6px">↗</a>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="d-flex align-items-center" v-if="totalPages > 1">
        <button class="btn btn-sm btn-outline-secondary mr-2" :disabled="page <= 1" @click="page--">← Prev</button>
        <span class="small text-muted">Page {{ page }} of {{ totalPages }}</span>
        <button class="btn btn-sm btn-outline-secondary ml-2" :disabled="page >= totalPages" @click="page++">Next →</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TxTable',
  props: {
    txs: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    accentColor: { type: String, default: '#627EEA' },
    pageSize: { type: Number, default: 50 },
  },
  data() {
    return { filterToken: '', filterDir: '', page: 1 };
  },
  computed: {
    tokenOptions() {
      return [...new Set(this.txs.map(t => t.token))].sort();
    },
    filteredTxs() {
      return this.txs.filter(tx => {
        if (this.filterToken && tx.token !== this.filterToken) return false;
        if (this.filterDir && tx.type !== this.filterDir) return false;
        return true;
      });
    },
    pagedTxs() {
      const start = (this.page - 1) * this.pageSize;
      return this.filteredTxs.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredTxs.length / this.pageSize));
    },
  },
  watch: {
    filterToken() { this.page = 1; },
    filterDir()   { this.page = 1; },
  },
  methods: {
    formatDate(ts) {
      if (!ts) return '—';
      return new Date(ts).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
    },
    shorten(addr) {
      if (!addr) return '—';
      return addr.length > 20 ? addr.slice(0, 10) + '…' + addr.slice(-6) : addr;
    },
    typeBadge(type) {
      if (type === 'IN')       return 'badge-success';
      if (type === 'OUT')      return 'badge-primary';
      if (type === 'FAILED')   return 'badge-danger';
      if (type === 'PENDING')  return 'badge-warning';
      return 'badge-secondary';
    },
  },
};
</script>

<style scoped>
/* Filter bar */
.input-group-text { border-radius: 6px 0 0 6px !important; font-size: 0.75rem !important; }
.form-control-sm { font-size: 0.78rem !important; }

/* Count */
.ml-auto { color: var(--text-muted, #52597a) !important; font-size: 0.75rem !important; }

/* Table */
.table td, .table th { white-space: nowrap; }
.table tbody tr:last-child td { border-bottom: none !important; }

/* Type badges */
.badge { border-radius: 6px !important; font-size: 0.66rem !important; padding: 3px 8px !important; }

/* Counterparty */
.small { font-size: 0.76rem !important; font-family: monospace; color: var(--text-2, #8892b0) !important; }

/* Explorer link */
.btn-xs { border-radius: 5px !important; line-height: 1 !important; }

/* Pagination */
.btn-sm { font-size: 0.76rem !important; }
.text-muted.small { font-size: 0.75rem !important; }

/* Spinner container */
.py-5 { padding-top: 3rem !important; padding-bottom: 3rem !important; }

/* Empty state */
.py-4 { padding-top: 2rem !important; padding-bottom: 2rem !important; }
</style>
